import DemographicType from '@/common/enums/DemographicType.enum';
import { Demographic } from '@/common/models';
import { DASH } from '@/globals';
import AudienceSegmentUtils from '@/utils/AudienceSegmentUtils';

const codes = {
  demographic: 'std:',
  predefinedSegment: 'p:',
  age: 'a:',
  gender: 'g:',
};

function isOfType(demographic: Demographic, type: DemographicType) {
  return demographic.type === type || demographic.subType === type;
}

function isBaseDemographic(demographic: Demographic): boolean {
  const demoTypes = [
    DemographicType.Gender,
    DemographicType.AgeGender,
    DemographicType.HouseHoldIncome,
    DemographicType.Age,
    DemographicType.Ethnicity,
  ];
  return (
    demoTypes.some((type) => isOfType(demographic, type)) &&
    !AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(demographic)
  );
}

function isCensusDemographic(demographic: Demographic): boolean {
  return isBaseDemographic(demographic) && !demographic.code.startsWith(codes.demographic);
}

function isGenderDemographic(demographic: Demographic): boolean {
  return (
    isOfType(demographic, DemographicType.Gender) &&
    !AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(demographic) &&
    (isCensusDemographic(demographic) ? /^[A-Z]/.test(demographic.code) : demographic.code.includes(codes.gender))
  );
}

function isAgeDemographic(demographic: Demographic): boolean {
  return (
    (isOfType(demographic, DemographicType.AgeGender) || isOfType(demographic, DemographicType.Age)) &&
    !AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(demographic) &&
    (isCensusDemographic(demographic)
      ? /^([A-Z] )?((\d+-\d+)|(\d+\+))$/.test(demographic.code)
      : demographic.code.includes(codes.age))
  );
}

function isAgeOnlyDemographic(demographic: Demographic): boolean {
  return (
    isOfType(demographic, DemographicType.Age) &&
    !AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(demographic) &&
    (isCensusDemographic(demographic)
      ? /^([A-Z] )?((\d+-\d+)|(\d+\+))$/.test(demographic.code)
      : demographic.code.includes(codes.age))
  );
}

function isIncomeDemographic(demographic: Demographic): boolean {
  return (
    isOfType(demographic, DemographicType.HouseHoldIncome) &&
    !AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(demographic)
  );
}

function isEthnicityDemographic(demographic: Demographic): boolean {
  return (
    isOfType(demographic, DemographicType.Ethnicity) &&
    !AudienceSegmentUtils.isDemographicALifesightPrecrunchedSegment(demographic)
  );
}

function getAgeRange(demographic: Demographic): { start: string; end: string } | null {
  if (!isAgeDemographic(demographic)) return null;

  let range: string[];
  if (demographic.code.startsWith(codes.demographic)) {
    range = demographic.code.split(`${codes.demographic} ${codes.age}`)[1].replace('[', '').replace(']', '').split(',');
  } else {
    const code = demographic.code.includes(' ') ? demographic.code.split(' ')[1] : demographic.code;
    if (code.includes('-')) {
      range = code.split('-');
    } else {
      range = code.split('+');
    }
  }

  const [start, end] = range;

  return {
    start: start === '' ? DASH : start,
    end: end === '' ? '+' : Number(end).toString(),
  };
}

function countCodes(demographic: Demographic): number {
  return [codes.predefinedSegment, codes.age, codes.gender].filter((code) => demographic?.code?.includes(code))?.length;
}

function isCombination(demographic: Demographic): boolean {
  return countCodes(demographic) > 1;
}

function getCodesFromCombinationCode(demographic: Demographic) {
  return demographic.code.split(`${codes.demographic} `)[1].split(' ');
}

export default {
  isGenderDemographic,
  isAgeDemographic,
  isAgeOnlyDemographic,
  isIncomeDemographic,
  isEthnicityDemographic,
  isBaseDemographic,
  isCensusDemographic,
  getAgeRange,
  isCombination,
  getCodesFromCombinationCode,
  codes,
};
